import React from "react";
import { Grid, Stack, Typography, alpha } from "@mui/material";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import {
  useGetBillingDashboardQuery,
  useGetSubscriptionQuery,
  useGetPlansQuery,
  useAssignSubscriptionMutation,
} from "../../../Api/platformApi";
import {
  AppPage,
  PageHeader,
  StatCard,
  CardSurface,
  ActionButton,
  StatusBadge,
} from "../../../Components/ui";
import { NAVY, GOLD, SUCCESS, ERROR, WARNING, INFO, TEXT_SECONDARY, BORDER } from "../../../theme/tokens";

const money = (v: any) =>
  "$" + Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

const BillingDashboard: React.FC = () => {
  const { data } = useGetBillingDashboardQuery();
  const { data: subData } = useGetSubscriptionQuery();
  const { data: plansData } = useGetPlansQuery();
  const [assign] = useAssignSubscriptionMutation();

  const d = data?.data ?? {};
  const sub = subData?.data;
  const plans = plansData?.data ?? [];
  const isSuper = d.mrr !== undefined; // super metrics shape

  return (
    <AppPage
      disablePaper
      header={
        <PageHeader
          icon={<DashboardRoundedIcon />}
          title="Billing"
          subtitle={isSuper ? "Platform-wide billing metrics" : "Your subscription & account balance"}
        />
      }
    >
      {isSuper ? (
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<TrendingUpRoundedIcon />} label="MRR" value={money(d.mrr)} color={INFO} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<BarChartRoundedIcon />} label="ARR" value={money(d.arr)} color={NAVY} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<CheckCircleOutlineRoundedIcon />} label="Active Subscriptions" value={String(d.active_subscriptions ?? 0)} color={SUCCESS} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<AccountBalanceWalletRoundedIcon />} label="Revenue (Month)" value={money(d.revenue_month)} color={SUCCESS} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<BarChartRoundedIcon />} label="Revenue (Total)" value={money(d.revenue_total)} color={NAVY} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<ErrorOutlineRoundedIcon />} label="Failed Payments" value={String(d.failed_payments ?? 0)} color={ERROR} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<ReceiptLongRoundedIcon />} label="Outstanding Invoices" value={String(d.outstanding_invoices ?? 0)} color={WARNING} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<AccountBalanceWalletRoundedIcon />} label="Outstanding $" value={money(d.outstanding_amount)} color={WARNING} />
          </Grid>
        </Grid>
      ) : (
        <>
          <Grid container spacing={2.5} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard icon={<CreditCardRoundedIcon />} label="Plan" value={d.plan ?? "—"} color={NAVY} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<CheckCircleOutlineRoundedIcon />}
                label="Status"
                value={<StatusBadge status={d.status} />}
                color={SUCCESS}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard icon={<ReceiptLongRoundedIcon />} label="Outstanding" value={money(d.outstanding_amount)} color={WARNING} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard icon={<AccountBalanceWalletRoundedIcon />} label="Paid (Total)" value={money(d.paid_total)} color={SUCCESS} />
            </Grid>
          </Grid>

          <Typography fontSize={18} fontWeight={800} color={NAVY} mb={2.5}>
            Available Plans
          </Typography>

          <Grid container spacing={3}>
            {plans.map((p: any) => {
              const isCurrent = sub?.subscription_plan_id === p.id;
              return (
                <Grid item xs={12} md={4} key={p.id}>
                  <CardSurface
                    sx={{
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      position: "relative",
                      ...(isCurrent
                        ? {
                            border: `2px solid ${GOLD}`,
                            boxShadow: `0 0 0 4px ${alpha(GOLD, 0.14)}, 0 8px 24px rgba(0,0,0,0.08)`,
                          }
                        : {}),
                    }}
                  >
                    {isCurrent && (
                      <StatusBadge status="active" label="Current Plan" tone="success" />
                    )}

                    <Typography fontSize={20} fontWeight={800} color={NAVY}>
                      {p.name}
                    </Typography>

                    <Stack direction="row" alignItems="baseline" spacing={0.5}>
                      <Typography fontSize={34} fontWeight={900} color={NAVY} lineHeight={1}>
                        {money(p.monthly_price)}
                      </Typography>
                      <Typography fontSize={13} color={TEXT_SECONDARY}>/mo</Typography>
                    </Stack>

                    <Stack
                      spacing={0.75}
                      sx={{
                        p: 2,
                        borderRadius: "10px",
                        bgcolor: alpha(NAVY, 0.04),
                        border: `1px solid ${BORDER}`,
                      }}
                    >
                      <QuotaRow label="SMS messages" value={p.included_sms} />
                      <QuotaRow label="WhatsApp messages" value={p.included_whatsapp} />
                      <QuotaRow label="Phone numbers" value={p.included_numbers} />
                      <QuotaRow label="Users" value={p.included_users} />
                    </Stack>

                    {(p.features_json ?? []).length > 0 && (
                      <Stack spacing={0.5}>
                        {(p.features_json ?? []).map((f: string, i: number) => (
                          <Stack key={i} direction="row" alignItems="center" spacing={1}>
                            <CheckRoundedIcon sx={{ fontSize: 14, color: SUCCESS }} />
                            <Typography fontSize={13} color={TEXT_SECONDARY}>{f}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                    )}

                    {!isCurrent && (
                      <ActionButton
                        fullWidth
                        onClick={() =>
                          assign({ subscription_plan_id: p.id, billing_cycle: "monthly" })
                        }
                      >
                        Choose Plan
                      </ActionButton>
                    )}
                  </CardSurface>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}
    </AppPage>
  );
};

const QuotaRow: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Typography fontSize={12} color={TEXT_SECONDARY}>{label}</Typography>
    <Typography fontSize={12} fontWeight={700} color={NAVY}>{Number(value).toLocaleString()}</Typography>
  </Stack>
);

export default BillingDashboard;
